import { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserView, QuotaProfileView, UserQuotaAssignmentView } from '../../api.js';
import { toCredits } from '../../components/AllocationPanel.js';
import { DataTable, type DataColumn } from '../../components/DataTable.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import {
  parseQuotaAssignmentCsv,
  type QuotaAssignmentCsvRow
} from './quota-assignment-csv.js';

type Filter = 'all' | 'assigned' | 'unassigned';

const PAGE_SIZE = 50;

/** Who is on which profile, and — just as important — what caps a member with
 *  no profile already has. The three filters are deliberately NOT the internal
 *  matched/fallback/conflict/unmapped enum: those words change meaning when
 *  rule-driven assignment lands, and an operator would have read the old ones. */
export function QuotaAssignmentReviewPanel({
  http,
  profiles,
  users,
  reloadToken,
  onToast,
  onChanged
}: {
  http: AdminHttpClient;
  profiles: QuotaProfileView[];
  /** The WHOLE tenant, not the visible page: a CSV names members the current
   *  page does not contain, and resolving against the page would report them
   *  as unknown. */
  users: AdminUserView[];
  /** Bumped by the profiles panel so a rename or retirement shows up here. */
  reloadToken: number;
  onToast: (toast: ToastMessage) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<UserQuotaAssignmentView[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvOutcomes, setCsvOutcomes] = useState<Record<number, string>>({});

  const activeProfiles = useMemo(
    () => profiles.filter((profile) => profile.status === 'active'),
    [profiles]
  );

  const fail = useCallback(
    (error: unknown) => onToast({ tone: 'error', message: describeError(error, t) }),
    [onToast, t]
  );

  const reload = useCallback(async () => {
    try {
      const page = await http.listUserQuotaAssignments({
        status: filter,
        ...(q.trim() ? { q: q.trim() } : {}),
        limit: PAGE_SIZE,
        offset
      });
      setRows(page.items);
      setTotal(page.total);
    } catch (error) {
      fail(error);
    }
  }, [fail, filter, http, offset, q]);

  useEffect(() => {
    void reload();
  }, [reload, reloadToken]);

  const assign = async (userId: string, quotaProfileId: string) => {
    setBusy(true);
    try {
      if (quotaProfileId) await http.assignQuotaProfile({ userId, quotaProfileId });
      else await http.clearQuotaProfilePin(userId);
      onToast({ tone: 'success', message: t('quotaAssignments.saved') });
      await reload();
      onChanged();
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const effectiveLabel = (row: UserQuotaAssignmentView) => {
    if (row.effectiveLimits.length === 0) return t('quotaAssignments.noEffectiveLimit');
    return row.effectiveLimits
      .map((limit) =>
        t('quotaAssignments.effectiveRow', {
          origin: t(`creditLimits.origin_${limit.origin}`),
          cycle: t(`creditLimits.cycle_${limit.cycle}`),
          model: limit.catalogModelId ?? t('creditLimits.modelAll'),
          credits: toCredits(limit.creditMillis)
        })
      )
      .join(' · ');
  };

  const columns: DataColumn<UserQuotaAssignmentView>[] = [
    {
      key: 'member',
      header: t('quotaAssignments.member'),
      render: (row) => row.name ?? row.email,
      sortValue: (row) => row.email
    },
    {
      key: 'profile',
      header: t('quotaProfiles.title'),
      render: (row) => (
        <select
          value={row.quotaProfileId ?? ''}
          onChange={(event) => void assign(row.userId, event.target.value)}
          disabled={busy}
          aria-label={t('quotaAssignments.assign')}
        >
          <option value="">{t('quotaAssignments.unassigned')}</option>
          {activeProfiles.map((profile) => (
            <option key={profile.id} value={profile.id}>
              {profile.name}
            </option>
          ))}
        </select>
      ),
      sortValue: (row) => row.profileCode ?? ''
    },
    {
      key: 'effective',
      header: t('quotaAssignments.effectiveLimit'),
      // The point of this column: "no profile" must never read as "no limit".
      render: (row) =>
        row.profileInactive ? (
          <span className="warning" title={t('quotaAssignments.profileInactiveHint')}>
            {`⚑ ${effectiveLabel(row)}`}
          </span>
        ) : (
          effectiveLabel(row)
        )
    },
    {
      key: 'assignedAt',
      header: t('quotaAssignments.assignedAt'),
      render: (row) => row.assignedAt?.slice(0, 10) ?? '—',
      sortValue: (row) => row.assignedAt ?? ''
    }
  ];

  const csvRows: QuotaAssignmentCsvRow[] = useMemo(
    () =>
      parseQuotaAssignmentCsv(csvText, {
        users: users.map((user) => ({ id: user.id, email: user.email })),
        profiles
      }),
    [csvText, profiles, users]
  );
  const csvValid = csvRows.filter((row) => !row.errorKey);
  const csvInvalidCount = csvRows.length - csvValid.length;

  const onCsvFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    void file.text().then(setCsvText);
  };

  const applyCsv = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const outcomes: Record<number, string> = {};
    try {
      for (const row of csvValid) {
        try {
          await http.assignQuotaProfile({
            userId: row.userId!,
            quotaProfileId: row.quotaProfileId!
          });
          outcomes[row.line] = t('quotaAssignments.csvApplied');
        } catch (error) {
          // Row-by-row outcomes: a partial apply must be visible, not silent.
          outcomes[row.line] = describeError(error, t);
        }
      }
      setCsvOutcomes(outcomes);
      await reload();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel quota-assignment-panel">
      <h2>{t('quotaAssignments.title')}</h2>
      <div className="quota-assignment-filters">
        <div className="segmented-control">
          {(['all', 'assigned', 'unassigned'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={filter === option ? 'active' : ''}
              aria-pressed={filter === option}
              onClick={() => {
                setFilter(option);
                setOffset(0);
              }}
              disabled={busy}
            >
              {t(`quotaAssignments.filter_${option}`)}
            </button>
          ))}
        </div>
        <label>
          <span>{t('quotaAssignments.search')}</span>
          <input
            value={q}
            onChange={(event) => {
              setQ(event.target.value);
              setOffset(0);
            }}
            disabled={busy}
            autoComplete="off"
          />
        </label>
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        empty={t('quotaAssignments.emptyState')}
        getRowKey={(row) => row.userId}
        defaultSort={{ key: 'member', dir: 'asc' }}
      />
      <div className="quota-assignment-paging">
        <span className="muted">
          {t('quotaAssignments.pageSummary', {
            from: total === 0 ? 0 : offset + 1,
            to: Math.min(offset + PAGE_SIZE, total),
            total
          })}
        </span>
        <button
          type="button"
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          disabled={busy || offset === 0}
        >
          {t('quotaAssignments.prevPage')}
        </button>
        <button
          type="button"
          onClick={() => setOffset(offset + PAGE_SIZE)}
          disabled={busy || offset + PAGE_SIZE >= total}
        >
          {t('quotaAssignments.nextPage')}
        </button>
      </div>

      <form className="quota-assignment-csv" onSubmit={applyCsv}>
        <h3>{t('quotaAssignments.csvTitle')}</h3>
        <p className="muted">{t('quotaAssignments.csvFormat')}</p>
        <label>
          <span>{t('quotaAssignments.csvFile')}</span>
          <input type="file" accept=".csv,text/csv" onChange={onCsvFile} disabled={busy} />
        </label>
        <label>
          <span>{t('quotaAssignments.csvText')}</span>
          <textarea
            value={csvText}
            placeholder="mel@acme.test,MANAGER"
            onChange={(event) => setCsvText(event.target.value)}
            disabled={busy}
            rows={6}
          />
        </label>
        {csvRows.length > 0 ? (
          <ul className="quota-assignment-csv-rows">
            {csvRows.map((row) => (
              <li key={row.line} className={row.errorKey ? 'warning' : undefined}>
                {`${row.line}. ${row.email} → ${row.profileCode}: `}
                {row.errorKey ? t(row.errorKey) : (csvOutcomes[row.line] ?? t('quotaAssignments.csvReady'))}
              </li>
            ))}
          </ul>
        ) : null}
        <button disabled={busy || csvValid.length === 0 || csvInvalidCount > 0}>
          {t('quotaAssignments.csvApply', { count: csvValid.length })}
        </button>
      </form>

      <p className="muted">{t('quotaProfiles.exposureWarning')}</p>
      <p className="muted">{t('creditLimits.noCounterReset')}</p>
    </section>
  );
}
